<template>
  <v-container>

    <v-row no-gutters>
      <v-col cols="12" class="text-right" >

          <dialog-date-picker
              calendar
              width="165px"
              :value="date"
              class="mt-2 mr-1"
              @change="onDateChange"
              ref="datePicker" />

        <v-btn rounded small class="pl-4" color="primary" @click="showStepper()">
          <v-icon left size="18">far fa-clock</v-icon>
          nueva cita
        </v-btn>
      </v-col>
    </v-row>

    <template v-if="loading">
      <v-row >
        <v-col cols="12">
          <v-sheet class="kim-card pa-4" min-height="200">
            <client-attention-skeleton :skeletons-qty="3"/>
          </v-sheet>
        </v-col>
      </v-row>
    </template>

    <v-row v-else justify="center" align="center">
      <v-col cols="12">
        <v-sheet class="kim-card pa-4" min-height="200" style="align-items: center">

          <v-row class="fill-height" no-gutters v-show="events.length === 0" justify="center">
            <v-col cols="12" class="body-2 mt-12 text-center">
                No hay citas para la fecha seleccionada.
            </v-col>
          </v-row>

          <v-row no-gutters v-show="events.length > 0">

            <template v-if="attendingEvents.length > 0">
              <v-col cols="12" >
                <client-attention-carousel
                    @click="onAppointmentClick"
                    :title="`En atención (${attendingEvents.length})`"
                    :appointments="attendingEvents" >

                  <template v-slot:appointment:details="{appointment}">
                    <b>En atención: </b>
                    <elapsed-time
                        :start-at="appointment.attention_started_at"
                    />
                  </template>

                </client-attention-carousel>
              </v-col>

              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </template>

            <template v-if="arrivedEvents.length > 0">
              <v-col cols="12" >
                <client-attention-carousel
                    @click="onAppointmentClick"
                    :title="`En espera (${arrivedEvents.length})`"
                    :appointments="arrivedEvents">

                  <template v-slot:appointment:details="{appointment}">
                    <b>Llegó hace: </b>
                    <elapsed-time :start-at="appointment.arrived_at"/>
                  </template>

                </client-attention-carousel>
              </v-col>

              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>

            </template>

            <template v-if="remainingEvents.length > 0">
              <v-col cols="12" >
                <client-attention-carousel
                    @click="onAppointmentClick"
                    :title="`Restantes (${remainingEvents.length})`"
                    :appointments="remainingEvents" >

                  <template v-slot:appointment:details="{appointment}">
                    <event-status-chip :outlined="false" :event-status="appointment.event_status"/>
                  </template>

                </client-attention-carousel>
              </v-col>

              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </template>

            <template v-if="cancelledEvents.length > 0">
              <v-col cols="12">
                <client-attention-carousel
                    disabled
                    @click="onAppointmentClick"
                    :title="`Cancelados (${cancelledEvents.length})`"
                    :appointments="cancelledEvents">

                  <template v-slot:appointment:details="{appointment}">
                    {{appointment.cancel_reason || '-'}}
                  </template>

                </client-attention-carousel>
              </v-col>

              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </template>

            <v-col cols="12" v-if="attendedEvents.length > 0">
              <client-attention-carousel
                  @click="onAppointmentClick"
                  :title="`Atendidos (${attendedEvents.length})`"
                  :appointments="attendedEvents" >

                <template v-slot:appointment:details="{appointment}">
                  <b>Atendid{{appointment.client.gender === 'f' ? 'a' : 'o'}} en: </b><time-difference
                      :start="appointment.attention_started_at"
                      :end="appointment.attention_ended_at"
                    />.


                </template>

              </client-attention-carousel>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
    </v-row>

    <appointment-provider-stepper
        internal
        @appointment-created="onAppointmentCreated"
        ref="stepper"/>

  </v-container>


</template>

<script>
  import AppointmentsMixin from "@/mixins/AppointmentsMixin";
  import AttentionClientListMixin from "@/mixins/AttentionClientListMixin";
  import ClientAttentionCarousel from "@/components/appointments/attention/home_screen/ClientAttentionCarousel";
  import {ARRIVED_STATUS_ID, ATTENDED_STATUS_ID, ATTENDING_STATUS_ID, CANCELLED_STATUS_ID} from "@/utils/ScheduleEvent";
  import AppointmentProviderStepper from "@/components/appointments/AppointmentProviderStepper";
  import DialogDatePicker from "@/components/common/DialogDatePicker";
  import ClientAttentionSkeleton from "@/components/appointments/attention/home_screen/ClientAttentionSkeleton";
  import ElapsedTime from "@/components/appointments/attention/ElapsedTime";
  import {getEventCancelReason} from "@/utils/Strings";
  import TimeDifference from "@/components/appointments/attention/TimeDifference";
  import EventStatusChip from "@/components/appointments/EventStatusChip";

  export default {
    name: "ClientAttentionHome",
    components: {
      EventStatusChip,
      TimeDifference,
      ElapsedTime,
      ClientAttentionSkeleton,
      DialogDatePicker,
      AppointmentProviderStepper,
      ClientAttentionCarousel
    },
    mixins: [AppointmentsMixin, AttentionClientListMixin],
    props: {
      specialistId: {
        type: Number
      },
    },
    beforeDestroy(){
      var vm = this
      if(vm.schedule){
        vm.leaveSockets()
      }
    },
    data() {
      return {
        snackBarClickDefaultAction: () => {},
      }
    },

    methods: {

      showDatePicker() {
        this.$refs.datePicker.show()
      },

      showStepper(defaultAppointment = null, allowModifying = false){
        this.$refs.stepper.show(defaultAppointment, allowModifying)
      },

    },
    computed: {
      individualProvider(){
        return !this.isInstitution
      },
      attendingEvents(){
        const vm = this
        return vm.formattedEvents.filter(event => {
          return event.event_status_id === ATTENDING_STATUS_ID
        })
      },
      arrivedEvents(){
        const vm = this
        return vm.formattedEvents.filter(event => {
          return event.event_status_id === ARRIVED_STATUS_ID
        })
      },
      attendedEvents(){
        const vm = this
        return vm.formattedEvents.filter(event => {
          return event.event_status_id === ATTENDED_STATUS_ID
        })
      },
      cancelledEvents(){
        const vm = this
        return vm.formattedEvents.filter(event => {
          return event.event_status_id === CANCELLED_STATUS_ID
        })
      },
      remainingEvents(){
        const vm = this
        return vm.formattedEvents.filter(event => {
          const {event_status_id} = event
          return event_status_id !== ATTENDED_STATUS_ID &&
              event_status_id !== ARRIVED_STATUS_ID &&
              event_status_id !== CANCELLED_STATUS_ID &&
              event_status_id !== ATTENDING_STATUS_ID
        })
      },
      formattedEvents(){
        const vm = this
        return vm.events.map(evt => {

          if(evt.event_status_id === CANCELLED_STATUS_ID){

            const {cancelled_events} = evt
            let {client, reason} = cancelled_events &&
            cancelled_events[cancelled_events.length - 1]

            if(!client) {
              client = {
                avatar: vm.getFakeAvatar(),
                first_name: 'Cita cancelada',
                last_name: '',
              }
            }

            evt.client = client
            evt.cancel_reason = getEventCancelReason(reason)

          }

          return evt


        }).sort((a, b) => vm.$moment(
            a.time, 'HH:mm') - vm.$moment(b.time, 'HH:mm')
        )
      }
    },

  }
</script>

<style scoped>

</style>
